import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { FvPromptComponent, IFvPromptConfig } from '@discocil/fv-components';
import { TranslateService } from '@ngx-translate/core';
import { filter, first, Subject, switchMap, takeWhile, timer } from 'rxjs';
import { GlobalFVComponent } from '../../interface/global-component.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { Logger } from 'src/app/core/interfaces/Logger';

@Injectable({
  providedIn: 'root',
})
export class GlobalPromptService
  extends GlobalFVComponent<FvPromptComponent>
  implements OnDestroy
{
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);

  private _currentConfig!: IFvPromptConfig;

  private _onOpen: Subject<void> = new Subject<void>();
  onOpen$ = this._onOpen.asObservable();

  get isOpened(): boolean {
    return this._currentComponent.isOpened;
  }

  get config(): IFvPromptConfig {
    return this._currentConfig;
  }

  open(config: IFvPromptConfig): void {
    if (this.isOpened) {
      this.close();
    }

    if (!config.validators) {
      config.validators = [];
    }

    config = this.translateConfig(config);
    this._currentConfig = config;
    this._currentComponent.open(config);
    this.detectModalOpen();
  }

  close(): void {
    if (!this._currentComponent.isClosing || this._currentComponent.isOpened) {
      this._currentComponent.close();
    }
  }

  private translateConfig(config: IFvPromptConfig): IFvPromptConfig {
    if (config.message) {
      config.message = this.translate.instant(config.message);
    }

    return config;
  }

  private detectModalOpen(): void {
    const pollingInterval = 100;
    const maxAttempts = 50;

    this.ngZone.runOutsideAngular(() => {
      timer(0, pollingInterval)
        .pipe(
          takeWhile((attempt) => attempt < maxAttempts),
          switchMap(() => [this.isOpened]),
          filter((isOpened) => isOpened),
          first(),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          this.ngZone.run(() => {
            this._onOpen.next();
          });
        });
    });
  }

  /**
   * Cleanup resources when service is destroyed
   * Complete Subject to prevent memory leaks
   */
  ngOnDestroy(): void {
    this.logger.debug('[GlobalPromptService] Cleaning up resources');

    if (this._onOpen) {
      this._onOpen.complete();
    }

    if (this._currentComponent && this.isOpened) {
      this.close();
    }
  }
}
